import { formatTimeOfDay, parseDate } from '@/lib/dates'
import { getMovementLabel } from '@/lib/movement-utils'
import { formatSpeedMph } from '@/lib/units'
import type { MapRegion, PathMarkerData } from '@/types/mapData'
import type { PathPoint } from '@/types/movement'

const MOVING_SPEED_THRESHOLD_MPS = 2.2
const STATIONARY_DISTANCE_THRESHOLD_M = 100

function isStationary(point: PathPoint): boolean {
    return !point.is_moving && point.speed <= MOVING_SPEED_THRESHOLD_MPS
}

export function getStationaryDuration(path: PathPoint[], mostRecent: PathPoint): number | null {
    if (!isStationary(mostRecent)) return null

    let earliestTimestamp = mostRecent.timestamp
    for (let i = path.length - 1; i >= 0; i--) {
        const point = path[i]
        if (point.timestamp === mostRecent.timestamp) continue
        if (
            !isStationary(point) ||
            haversineDistance(
                point.latitude,
                point.longitude,
                mostRecent.latitude,
                mostRecent.longitude
            ) > STATIONARY_DISTANCE_THRESHOLD_M
        ) {
            break
        }
        earliestTimestamp = point.timestamp
    }

    const startMs = parseDate(earliestTimestamp).getTime()
    const nowMs = Date.now()
    const durationSeconds = Math.floor((nowMs - startMs) / 1000)
    return durationSeconds > 0 ? durationSeconds : null
}

export function formatCompactDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

export const DEFAULT_REGION: MapRegion = {
    latitude: 38,
    longitude: -92,
    latitudeDelta: 17,
    longitudeDelta: 17,
}

export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLon = ((lon2 - lon1) * Math.PI) / 180
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
}

export function filterMarkersByDistance(
    markers: PathMarkerData[],
    minDistanceMeters: number
): PathMarkerData[] {
    if (markers.length === 0) return markers
    const result: PathMarkerData[] = [markers[0]]
    for (let i = 1; i < markers.length; i++) {
        const last = result[result.length - 1]
        const current = markers[i]
        const distance = haversineDistance(
            last.coordinate.latitude,
            last.coordinate.longitude,
            current.coordinate.latitude,
            current.coordinate.longitude
        )
        if (distance >= minDistanceMeters) {
            result.push(current)
        }
    }
    return result
}

export function buildPathPointDescription(point: PathPoint): string {
    const time = formatTimeOfDay(point.timestamp)
    const label = getMovementLabel(point.movement_type, point.is_moving, point.heading, point.speed)
    if (label === 'stationary') return `${time} stationary`
    const speed = formatSpeedMph(point.speed)
    return label ? `${time} ${speed} (${label})` : `${time} ${speed}`
}

interface Coordinate {
    latitude: number
    longitude: number
}

export function calculateRegionFromCoordinates(coords: Coordinate[]): MapRegion {
    if (coords.length === 0) return DEFAULT_REGION

    const lats = coords.map(c => c.latitude)
    const lngs = coords.map(c => c.longitude)

    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)
    const minLng = Math.min(...lngs)
    const maxLng = Math.max(...lngs)

    const latDelta = (maxLat - minLat) * 1.5 || 0.01
    const lngDelta = (maxLng - minLng) * 1.5 || 0.01

    return {
        latitude: (minLat + maxLat) / 2,
        longitude: (minLng + maxLng) / 2,
        latitudeDelta: Math.max(latDelta, 0.01),
        longitudeDelta: Math.max(lngDelta, 0.01),
    }
}

export function computeSegmentOpacity(
    timestamp: string,
    referenceTime: Date,
    fullOpacityMinutes: number,
    minOpacity: number
): number {
    const ageMs = referenceTime.getTime() - new Date(timestamp).getTime()
    const ageMinutes = ageMs / 60_000
    if (ageMinutes <= fullOpacityMinutes) return 1.0
    const fadeDurationMinutes = 120
    const fadeProgress = Math.min((ageMinutes - fullOpacityMinutes) / fadeDurationMinutes, 1)
    return 1.0 - fadeProgress * (1.0 - minOpacity)
}

export function getMinDistanceMeters(latitudeDelta: number | null): number {
    if (latitudeDelta === null) return 100
    const metersPerDegree = 111000
    const viewportMeters = latitudeDelta * metersPerDegree
    return viewportMeters / 15
}
